/**
 * Grit Mile landing page — single-page lead-capture form.
 *
 * Features:
 *   - Hero image placeholder (replace public/hero.svg with real asset)
 *   - A/B variant headline + button label (9 variants via cookie hash)
 *   - Email input with client-side validation
 *   - Impression telemetry once per session
 *   - Conversion telemetry + API lead submission on form submit
 */

import { type FormEvent, useEffect, useRef, useState } from "react";
import { postLead } from "./api.js";
import { getOrCreateVisitorCookie, getVariantIndex } from "./cookie.js";
import { fireConversion, fireImpression } from "./telemetry.js";
import { BRAND_TAG, VARIANTS } from "./variants.js";

// ── Email validation ──────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_MAX_LENGTH = 254;

function isValidEmail(email: string): boolean {
  return email.length > 0 && email.length <= EMAIL_MAX_LENGTH && EMAIL_REGEX.test(email);
}

// ── Referrer source (hostname only, no PII) ───────────────────────────────────

function getReferrerSource(): string {
  try {
    if (!document.referrer) return "direct";
    return new URL(document.referrer).hostname;
  } catch {
    return "direct";
  }
}

// ── Fallback variant (used when variantIndex is out of range) ─────────────────

const FALLBACK_VARIANT = {
  headline: "Train harder. And smarter.",
  buttonLabel: "I'm in!",
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

type Status = "idle" | "submitting" | "success" | "error";

export default function App() {
  const visitorId = useRef<string | null>(null);
  const variantIndex = useRef<number>(0);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");

  // Assign variant and fire impression on mount
  useEffect(() => {
    const vid = getOrCreateVisitorCookie();
    visitorId.current = vid;
    const idx = getVariantIndex(vid);
    variantIndex.current = idx;

    // Fire impression once per session (telemetry.ts deduplicates via sid cookie)
    fireImpression(idx, getReferrerSource()).catch(() => {
      // Non-fatal — telemetry failure must not break the page
    });
  }, []);

  const variant = VARIANTS[variantIndex.current] ?? FALLBACK_VARIANT;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError(null);
    setStatus("submitting");

    const source = getReferrerSource();
    const userAgent = navigator.userAgent.slice(0, 128);
    const idx = variantIndex.current;

    postLead({ email, variantId: idx, source, userAgent })
      .then(() => fireConversion(idx, email))
      .then(() => {
        setStatus("success");
      })
      .catch(() => {
        setStatus("error");
      });
  };

  if (status === "success") {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4" style={{ color: "var(--color-accent)" }}>
            You&apos;re on the list!
          </h2>
          <p style={{ color: "var(--color-muted)" }}>
            We&apos;ll reach out when Grit Mile is ready for you.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-lg text-center">
        {/* Brand mark */}
        <img
          src="/icon.svg"
          alt="Grit Mile"
          className="mx-auto mb-8 w-64 h-64"
          width={256}
          height={256}
        />

        {/* Variant headline */}
        <h1
          className="text-4xl font-bold mb-4 leading-tight"
          style={{ color: "var(--color-text)" }}
        >
          {variant.headline}
        </h1>

        {/* Fixed brand tag line — not A/B-tested */}
        <p className="text-xl font-medium mb-4" style={{ color: "var(--color-text)" }}>
          {BRAND_TAG}
        </p>

        <p className="text-lg mb-8" style={{ color: "var(--color-muted)" }}>
          AI-powered coaching that adapts to your training load, recovery, and goals.
        </p>

        {/* Signup form */}
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <input
              type="email"
              name="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              aria-label="Email address"
              aria-describedby={emailError ? "email-error" : undefined}
              className="w-full px-4 py-3 rounded-lg border text-base"
              style={{
                backgroundColor: "var(--color-surface)",
                borderColor: emailError ? "#ef4444" : "var(--color-border)",
                color: "var(--color-text)",
              }}
              required
            />
            {emailError && (
              <p id="email-error" className="text-sm text-left" style={{ color: "#ef4444" }}>
                {emailError}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full px-4 py-3 rounded-lg font-semibold text-base transition-opacity disabled:opacity-60"
            style={{ backgroundColor: "var(--color-accent)", color: "#0a0a0a" }}
          >
            {status === "submitting" ? "Joining…" : variant.buttonLabel}
          </button>

          {status === "error" && (
            <p className="text-sm" style={{ color: "#ef4444" }}>
              Something went wrong. Please try again.
            </p>
          )}

          <p className="text-xs text-left" style={{ color: "var(--color-muted)" }}>
            By signing up you agree to receive emails about Grit Mile. We won&apos;t share your
            email. Unsubscribe anytime. See our{" "}
            <a
              href="/privacy.html"
              style={{ color: "var(--color-muted)", textDecoration: "underline" }}
            >
              Privacy Policy
            </a>
            .
          </p>
        </form>

        <p className="mt-6 text-xs" style={{ color: "var(--color-muted)" }}>
          No spam. Unsubscribe any time.
        </p>
      </div>

      {/* Footer */}
      <footer
        className="w-full text-center py-4 mt-8 text-xs"
        style={{ color: "var(--color-muted)", borderTop: "1px solid var(--color-border)" }}
      >
        <a
          href="/privacy.html"
          style={{ color: "var(--color-muted)", textDecoration: "underline" }}
        >
          Privacy Policy
        </a>
      </footer>
    </main>
  );
}
